import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, X, Building, Plus } from 'lucide-react';
import { TextField } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { Button } from '@atoms';
import { trpc } from '@lib/trpc';
import { SupplierRecord } from '@types';
import { useAuth } from '@contexts/AuthContext';
import { usePermission, SUPPLIER_PERMISSIONS } from '@hooks/usePermissions';
import { useABNValidation } from '@hooks/useABNValidation';
import toast from 'react-hot-toast';
import { muiTheme } from '@/theme/muiTheme';


export const SupplierCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState<Partial<SupplierRecord>>({
    name: '',
    abn: '',
    phone: '',
    email: '',
    terms: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  // ABN validation
  const { abnError, isValidating: isValidatingABN, validateABN } = useABNValidation(formData.abn || '');

  // Check if user has permission to create suppliers
  const { hasPermission: canCreateSupplier, isLoading: permissionLoading } = usePermission(SUPPLIER_PERMISSIONS.CREATE);

  // Create mutation - MUST be called before any conditional returns
  const createSupplierMutation = trpc.supplier.create.useMutation({
    onSuccess: () => {
      toast.success('Supplier created successfully');
      navigate('/suppliers');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to create supplier');
    },
  });

  // Redirect users without permission
  React.useEffect(() => {
    if (user && !permissionLoading && !canCreateSupplier) {
      toast.error('You do not have permission to create suppliers');
      navigate('/suppliers');
    }
  }, [user, permissionLoading, canCreateSupplier, navigate]);

  // Show loading while checking permissions
  if (permissionLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="w-8 h-8 border-4 border-accent-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }


  const handleInputChange = (field: keyof SupplierRecord, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    // Validate required fields
    if (!formData.name?.trim()) {
      toast.error('Supplier name is required');
      return;
    }

    // Validate ABN if provided
    if (formData.abn?.trim()) {
      const isABNValid = await validateABN(formData.abn.trim());
      if (!isABNValid) {
        toast.error(abnError || 'ABN validation failed');
        return;
      }
    }
    
    setIsLoading(true);
    try {
      await createSupplierMutation.mutateAsync({
        name: formData.name.trim(),
        abn: formData.abn?.trim() || undefined,
        phone: formData.phone?.trim() || undefined,
        email: formData.email?.trim() || undefined,
        terms: formData.terms?.trim() || undefined,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/suppliers');
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-secondary-200 -mx-4 px-4 mb-8">
        <div className="py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <Button
                variant="secondary"
                size="sm"
                icon={ArrowLeft}
                onClick={handleCancel}
              >
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-secondary-900">
                  Add New Supplier
                </h1>
                <p className="text-sm text-secondary-600">
                  Create a new supplier record
                </p>
              </div>
            </div>
            <div className="flex space-x-3">
              <Button
                variant="secondary"
                size="md"
                icon={X}
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                icon={Save}
                loading={isLoading}
                disabled={isValidatingABN || !!abnError}
                onClick={handleSave}
              >
                Create Supplier
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-8">
        {/* Main Form */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-secondary-900 mb-6 flex items-center">
            <Building className="w-5 h-5 mr-2 text-primary-500" />
            Supplier Details
          </h2>
          
          <ThemeProvider theme={muiTheme}>
            <div className="space-y-6">
              {/* Explanation */}
              <div className="bg-primary-50 border border-primary-200 rounded-md p-4">
                <h3 className="text-sm font-medium text-primary-800 mb-2">Create New Supplier</h3>
                <p className="text-sm text-primary-700">
                  Enter the supplier's contact information, business details, and payment terms.
                  This information will be used for invoice processing and reporting.
                </p>
              </div>

              {/* Supplier Name */}
              <TextField
                label="Supplier Name *"
                variant="outlined"
                size="small"
                value={formData.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value.toUpperCase())}
                required
                fullWidth
                helperText="Enter the full legal name of the supplier (automatically converted to uppercase)"
                inputProps={{
                  style: { textTransform: 'uppercase' }
                }}
              />

              {/* ABN */}
              <TextField
                label="ABN"
                variant="outlined"
                size="small"
                value={formData.abn || ''}
                onChange={(e) => handleInputChange('abn', e.target.value)}
                placeholder="e.g., 12345678901"
                fullWidth
                error={!!abnError}
                helperText={abnError || "Australian Business Number (optional)"}
                InputProps={{
                  endAdornment: isValidatingABN ? (
                    <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                  ) : null,
                }}
              />

              {/* Phone */}
              <TextField
                label="Phone Number"
                type="tel"
                variant="outlined"
                size="small"
                value={formData.phone || ''}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="e.g., (07) 3123 4567"
                fullWidth
                helperText="Primary contact phone number (optional)"
              />

              {/* Email */}
              <TextField
                label="Email Address"
                type="email"
                variant="outlined"
                size="small"
                value={formData.email || ''}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="e.g., accounts@supplier.com.au"
                fullWidth
                helperText="Primary contact email address (optional)"
              />

              {/* Payment Terms */}
              <TextField
                label="Payment Terms"
                variant="outlined"
                size="small"
                value={formData.terms || ''}
                onChange={(e) => handleInputChange('terms', e.target.value)}
                placeholder="e.g., NET 30, COD, 30 EOM"
                fullWidth
                helperText="Payment terms and conditions (optional)"
              />

            </div>
          </ThemeProvider>
        </div>

        {/* Additional Information Panel */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-secondary-900 mb-4 flex items-center">
            <Plus className="w-5 h-5 mr-2 text-primary-500" />
            Getting Started
          </h3>
          <div className="bg-secondary-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-secondary-800 mb-2">After creating this supplier:</h4>
            <ul className="text-sm text-secondary-600 space-y-1 list-disc list-inside">
              <li>You can edit supplier details anytime from the supplier list</li>
              <li>Invoices will automatically be linked to this supplier when processed</li>
              <li>The system will track invoice counts and last invoice dates</li>
              <li>You can view all supplier activity in the audit log</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
